import { useMemo } from "react";

import { useGetTenantsByIdsQuery } from "src/core/api/tenant";
import { TenantOption } from "src/core/types/tenant";
import { buildTenantIdsKey } from "src/core/utils/header/tenant.utils";

type UseEnrichTenantNamesResult = {
  tenants: TenantOption[];
  isLoading: boolean;
};

/**
 * Hook : useEnrichTenantNames :
 * Rôle fonctionnel :
 * - Enrichir (en lecture) les libellés des tenants affichés dans l'UI (header, select...).
 *
 * Pourquoi :
 * - Les tenants provenant du contexte peuvent contenir un "name" incomplet/technique.
 * - On récupère les noms "officiels" via tenantService.
 *
 * IMPORTANT :
 * - Ce hook est "pur" côté state applicatif : il NE modifie PAS le contexte Tenant.
 * - Il retourne une version enrichie de la liste + un état de chargement.
 *
 * Sécurité :
 * - Protection contre les réponses obsolètes : on vérifie que la réponse correspond
 *   bien au jeu d'IDs courant avant d'appliquer l'enrichissement.
 */
export const useEnrichTenantNames = (
  availableTenants: TenantOption[]
): UseEnrichTenantNamesResult => {
  const tenantIds = useMemo(() => availableTenants.map((t) => t.id), [availableTenants]);
  const tenantIdsKey = useMemo(() => buildTenantIdsKey(availableTenants), [availableTenants]);

  const { data, isFetching } = useGetTenantsByIdsQuery(
    { tenantIds },
    {
      // Pas d'appel si pas de tenants
      skip: tenantIds.length === 0,
    }
  );

  const enrichedTenants = useMemo<TenantOption[]>(() => {
    // Tant que l'on n'a pas de données, on renvoie la liste telle quelle (pas de flicker).
    if (!data?.length || !tenantIdsKey) return availableTenants;

    /**
     * Protection anti-race condition :
     * On vérifie que la réponse contient exactement le même set d'IDs
     * que celui attendu au moment du rendu.
     */
    const dataIds = new Set(data.map((t) => t.tenantId));
    const currentIds = new Set(tenantIds);
    const isSameSet =
      dataIds.size === currentIds.size && [...dataIds].every((id) => currentIds.has(id));

    if (!isSameSet) {
      return availableTenants;
    }

    const tenantsMap = new Map(data.map((t) => [t.tenantId, t.tenantName] as const));

    return availableTenants.map((tenant) => ({
      id: tenant.id,
      // On remplace uniquement le nom si on a une valeur issue du tenantService
      name: tenantsMap.get(tenant.id) ?? tenant.name,
    }));
  }, [availableTenants, data, tenantIds, tenantIdsKey]);

  return { tenants: enrichedTenants, isLoading: isFetching };
};
